import { Box, ListItem, Stack, TextField, Tooltip } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import { FC, ReactElement, useEffect } from "react";
import { DetectPattern } from "@/gen/api/encrypt";
import { FormToken, useZSliceUnderContext } from "@/utils/zustand-form";
import { usePlainStateFormUtils } from "../input/usePlainStateFormUtils";
import { dpSchema } from "./InfoTypeEditor.types";
import { CollectEditsMetadata, CollectEditsProtocol } from "@/utils/zustand-form/ext/collectEdits";

const DetectPatternLine : FC<{
  parentToken: FormToken,
  className?: string,
  r_key: string,
  prevData: DetectPattern,
  actionButton: ReactElement,
}> = ({ className, r_key, prevData, actionButton, parentToken }) => {
  const utils = useZSliceUnderContext({
    parentToken: parentToken ?? null,
    pathFragment: r_key,
    prevData,
    stageInit: () => ({ ...prevData }),
  });
  const { states, setStates, installProtocolMetadata } = utils;

  useEffect(() => {
    setStates(v => ({...v, prevData, stage: {...prevData}, errors:{}, touched:{} }))
  }, [prevData, setStates]);
  const { trySubmitValidate, fieldProps } = usePlainStateFormUtils(states, setStates, dpSchema);

  useEffect(() => {
    return installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: async () => { 
        try {
          await trySubmitValidate() 
        } catch (error) {
          throw new Error('Validation error', { cause: error })
        } 
      }
    })
  }, [installProtocolMetadata, trySubmitValidate])

  const isDirty = (
    states.prevData.name !== states.stage.name ||
    states.prevData.score !== states.stage.score ||
    states.prevData.regex !== states.stage.regex
  );
  (states as any).isDirty = isDirty;
  return (
    <ListItem className={className ?? (Object.keys(states.touched).length > 0 ? 'dirty' : undefined)}>
      <Stack className="line-box" direction="row" spacing={4} >
        <TextField label="Name"  variant="standard" size="small" {...fieldProps('name')} multiline/>
        <TextField variant="standard" size="small" type="number" sx={{ width: 60, '& p.MuiFormHelperText-root.Mui-error': { width: 200 } }}
          label={
            <Stack direction="row" alignItems="start">
              Score
              <Tooltip title="A matching score indicates how likely the matching content is actually of this Info Type. The score may be modified by the Context Rules below, if any. A final score of 0.5 or above is deemed that the matching content is of this Info Type.">
                <Box>
                  <InfoIcon sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                </Box>
              </Tooltip>
            </Stack>}
          {...fieldProps('score', (x: any) => {
            if (!x) { return undefined }
            let n = +x
            if (Number.isNaN(n)) { return 0 }
            n = parseFloat(n.toFixed(2))
            return (
                n < 0 ? 0
              : n > 1 ? 1
              : n)
          })}
          inputProps={{ step: 0.01, max: 1, min: 0, }}
        />
        <TextField label="Regular Expression" variant="standard" size="small" {...fieldProps('regex')} multiline />
      </Stack>
      {actionButton}
    </ListItem>
  )
}

export default DetectPatternLine;